// ============================================================
// 四柱关系分析 —— 柱际干支五行生克 + 经典合冲刑害格局
// 规则出处（通行古法，非杜撰）：
//  · 天干五合：甲己合土、乙庚合金、丙辛合水、丁壬合木、戊癸合火（渊海子平）
//  · 地支六合：子丑、寅亥、卯戌、辰酉、巳申、午未
//  · 地支相冲：子午、丑未、寅申、卯酉、辰戌、巳亥
//  · 地支相刑：子卯（无礼）、寅巳申（恃势）、丑戌未（无恩）、辰午酉亥自刑
//  · 地支相害：子未、丑午、寅巳、卯辰、申亥、酉戌
//  · 天克地冲／天比地冲：干支双向同判（三命通会论战克）
//  · 宫位框架：年＝祖上·早年，月＝父母·青年（提纲），
//    日＝自身·配偶（中日），时＝子女·晚年归宿 —— 传统分段法
// ============================================================

import { GAN_WX, ZHI_WX } from './tables.js';

export const GAN = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
export const SHENG_NEXT = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };

// ---- 天干五合 ----
const GAN_HE = { '甲己': '土', '乙庚': '金', '丙辛': '水', '丁壬': '木', '戊癸': '火' };
function ganHe(a, b) {
  const k1 = a + b, k2 = b + a;
  return GAN_HE[k1] ? { he: GAN_HE[k1], name: `${a}${b}合${GAN_HE[k1]}` }
       : GAN_HE[k2] ? { he: GAN_HE[k2], name: `${b}${a}合${GAN_HE[k2]}` } : null;
}

// ---- 地支关系 ----
const ZHI_LIUHE = ['子丑', '寅亥', '卯戌', '辰酉', '巳申', '午未'];
const ZHI_CHONG = [['子', '午'], ['丑', '未'], ['寅', '申'], ['卯', '酉'], ['辰', '戌'], ['巳', '亥']];
const ZHI_HAI = [['子', '未'], ['丑', '午'], ['寅', '巳'], ['卯', '辰'], ['申', '亥'], ['酉', '戌']];
const ZHI_XING = [
  { pair: ['子', '卯'], name: '无礼之刑' },
  { trio: ['寅', '巳', '申'], name: '恃势之刑' },
  { trio: ['丑', '戌', '未'], name: '无恩之刑' },
  { self: true, zs: ['辰', '午', '酉', '亥'], name: '自刑' },
];

export function zhiRelation(a, b) {
  const k1 = a + b, k2 = b + a;
  if (ZHI_LIUHE.includes(k1) || ZHI_LIUHE.includes(k2)) return { type: '六合', tag: '合', good: true, text: `${a}${b}六合` };
  const chong = ZHI_CHONG.find(([x, y]) => (x === a && y === b) || (x === b && y === a));
  if (chong) return { type: '相冲', tag: '冲', good: false, text: `${a}${b}相冲` };
  const hai = ZHI_HAI.find(([x, y]) => (x === a && y === b) || (x === b && y === a));
  if (hai) return { type: '相害', tag: '害', good: false, text: `${a}${b}相害` };
  for (const x of ZHI_XING) {
    if (x.trio && x.trio.includes(a) && x.trio.includes(b)) return { type: '相刑', tag: '刑', good: false, text: `${a}${b}相刑（${x.name}）` };
    if (x.self && a === b && x.zs.includes(a)) return { type: '自刑', tag: '刑', good: false, text: `${a}${a}自刑` };
  }
  if (a === b) return { type: '伏吟', tag: '同', good: null, text: `${a}${a}并见` };
  return null;
}

// ---- 五行生克方向：'生'(A生B) '受生'(B生A) '克'(A克B) '受克'(B克A) '比' ----
export function wxRel(a, b) {
  if (!a || !b) return '';
  if (a === b) return '比';
  if (SHENG_NEXT[a] === b) return '生';
  if (SHENG_NEXT[b] === a) return '受生';
  if (SHENG_NEXT[SHENG_NEXT[a]] === b) return '克';
  if (SHENG_NEXT[SHENG_NEXT[b]] === a) return '受克';
  return '';
}

const REL_WORD = {
  生: { word: '相生', desc: '前者滋生后者，气由前者流向后者：前者为付出、化泄的一方，后者为受生得益的一方。' },
  受生: { word: '受生', desc: '前者受后者所生，气由后者流向前者：前者得后者滋养扶助。' },
  克: { word: '相克', desc: '前者克制后者：前者占主导制约之力，后者受管束；过旺则防压制太过。' },
  受克: { word: '受克', desc: '前者受后者克制：前者受后者约束压制，宜静不宜动。' },
  比: { word: '比和', desc: '两者同气并肩：既相扶相助，亦有分夺之势。' },
};

// ---- 十神（以日干为基准）----
export function tenGod(dayGan, otherGan) {
  const dy = GAN.indexOf(dayGan), oy = GAN.indexOf(otherGan);
  if (dy < 0 || oy < 0) return '';
  const sameYY = dy % 2 === oy % 2;
  const dWx = GAN_WX[dayGan], oWx = GAN_WX[otherGan];
  if (dWx === oWx) return sameYY ? '比肩' : '劫财';
  if (SHENG_NEXT[dWx] === oWx) return sameYY ? '食神' : '伤官';
  if (SHENG_NEXT[oWx] === dWx) return sameYY ? '偏印' : '正印';
  if (SHENG_NEXT[SHENG_NEXT[dWx]] === oWx) return sameYY ? '偏财' : '正财';
  return sameYY ? '七杀' : '正官';
}

// ---- 宫位角色 ----
const ROLE = { 年柱: '祖上根基·早年', 月柱: '父母兄弟·青年（提纲）', 日柱: '自身配偶·中年', 时柱: '子女晚景·归宿' };

/**
 * 六组柱际两两分析
 */
export function analyzePillarRelations(pillars) {
  const out = [];
  for (let i = 0; i < pillars.length; i++) {
    for (let j = i + 1; j < pillars.length; j++) {
      const A = pillars[i], B = pillars[j];
      const gRel = wxRel(GAN_WX[A.gan], GAN_WX[B.gan]);
      const zRelWx = wxRel(ZHI_WX[A.zhi], ZHI_WX[B.zhi]);
      const he = ganHe(A.gan, B.gan);
      const zh = zhiRelation(A.zhi, B.zhi);

      // 组合格局
      const tags = [];
      if (he) tags.push({ name: he.name, good: true, text: '天干五合，两柱之气相结，主关联亲密、事易牵合（合化需通根月令方真）。' });
      if (zh) tags.push({ name: zh.text, good: zh.good, text: TAG_TEXT[zh.type] });
      if (gRel === '克' && zRelWx === '克') tags.push({ name: '天克地冲', good: false, text: '天干地支双双相克（天克地冲）：两柱所主之事激烈交战，为六亲宫位之大动荡，逢之多变动波折。' });
      if ((gRel === '受克' && zRelWx === '受克')) tags.push({ name: '天克地冲（反向）', good: false, text: '此柱被彼柱天克地冲，此柱所主之人事物受冲击明显。' });

      out.push({
        a: A.label, b: B.label,
        aGan: A.gan, bGan: B.gan, aZhi: A.zhi, bZhi: B.zhi,
        ganText: `${A.gan}(${GAN_WX[A.gan]}) ${REL_WORD[gRel].word} ${B.gan}(${GAN_WX[B.gan]})`,
        zhiText: `${A.zhi}(${ZHI_WX[A.zhi]}) ${REL_WORD[zRelWx].word} ${B.zhi}(${ZHI_WX[B.zhi]})`,
        gRel, zRelWx,
        tags,
        meaning: pairMeaning(A.label, B.label, gRel, zRelWx),
      });
    }
  }
  return out;
}

const TAG_TEXT = {
  '六合': '地支六合，两支暗中有情，主其宫位人事亲近和合、事可合作。', 
  '相冲': '地支相冲，一动一破：两宫所主之人事物多变动、分离或激化；冲提纲尤重。',
  '相害': '地支相害（六害），主暗伤妨害：两宫之间易有暗中损耗、是非牵扯。',
  '相刑': '地支相刑，主刑伤纠葛、恩中生怨；两宫之事易纠缠反复。',
  '自刑': '自刑者自寻烦恼，主内耗犹疑。',
  '伏吟': '地支并见为伏吟，「反吟伏吟泪吟吟」，主其事呻吟反复、停滞忧郁。',
};

function pairMeaning(aLabel, bLabel, gRel, zRelWx) {
  const ra = ROLE[aLabel], rb = ROLE[bLabel];
  const lines = [];
  lines.push(`【宫位】${aLabel}主${ra}；${bLabel}主${rb}。`);
  lines.push(`【天干】${REL_WORD[gRel].desc}`);
  if (zRelWx !== gRel) lines.push(`【地支】${REL_WORD[zRelWx].desc}`);
  // 方向总断（以天干主事，传统「我生者泄、生我者益」论）
  if (aLabel === '日柱' || bLabel === '日柱') {
    const other = aLabel === '日柱' ? bLabel : aLabel;
    const INV = { 生: '受生', 受生: '生', 克: '受克', 受克: '克', 比: '比' };
    const dir = aLabel === '日柱' ? gRel : INV[gRel];
    const map = {
      '年柱': { 生: '日主之气泄向祖上早年宫：多为出身家庭操心出力，早年担责。', 受生: '祖上根基生扶日主：得祖荫长辈之助，早年顺遂有依。', 克: '日主克制祖上宫：自主早立，不依家业，与家中长辈意见易分歧。', 受克: '祖上宫克抑日主：早年家教严、束缚多，白手起家之象。', 比: '日主与祖上宫比和：家风自立，早年平顺自我。' },
      '月柱': { 生: '日主泄气于提纲：为父母兄弟、青年事业付出操劳，青中年多劳。', 受生: '提纲生扶日主：得父母师长之力，青年运得助，根基厚。', 克: '日主克提纲：自立门户不服管束，青年期与家庭或体制有张力（亦主能掌财权）。', 受克: '提纲克抑日主：青年期压力大、规矩重，谋事多磨而后成。', 比: '日主与提纲比和：兄弟姐妹同心，青年平顺。' },
      '时柱': { 生: '日主生时柱：疼爱子女、为晚景经营付出；中晚年心有所归，子女得荫。', 受生: '时柱生扶日主：子女晚辈反哺，晚年有靠，归宿安稳。', 克: '日主克时柱：对子女晚辈要求严、掌控欲强；晚年仍劳心操持。', 受克: '时柱克抑日主：晚辈或晚年之事牵累自身，晚年宜宽心少管。', 比: '时柱与日主比和：晚年与子女如友，归宿自在。' },
    }[other];
    if (map && map[dir]) lines.push(`【总断】${map[dir]}`);
  } else {
    lines.push('【总断】两宫之气以生扶为美、冲克为忌：相生则其宫位人事相互助益，相克冲刑则多磨砺变动。');
  }
  return lines.join('');
}
